export type VerifiedProperty = {
  id: string;
  name: string;
  location: string;
  area: '30A' | 'Panama City Beach';
  communities: PropertyCommunity[];
  bedrooms: number;
  bathrooms: string;
  sleeps: number;
  beachfront: boolean;
  privatePool: boolean;
  golfCart: boolean;
  bikes: boolean;
  elevator: boolean;
  rooftop: boolean;
  gulfViews: boolean;
  carriageHouse: boolean;
  summerKitchen: boolean;
  outdoorLiving: boolean;
  highlights: string[];
  verifiedAmenityPhrases: string[];
  summarySentence: string;
  image: string;
  tag: string;
  blurb: string;
  sourceUrl: string;
  petPolicy?: string;
  status: 'verified';
};

export type PropertyCommunity =
  | 'Panama City Beach'
  | '30A'
  | 'Rosemary Beach'
  | 'WaterSound'
  | 'Watercolor'
  | 'Dune Allen'
  | 'Santa Rosa Beach';

export const verifiedProperties: VerifiedProperty[] = [
  {
    id: 'belvedere',
    name: 'The Belvedere',
    location: 'Rosemary Beach',
    area: '30A',
    communities: ['30A', 'Rosemary Beach'],
    bedrooms: 7,
    bathrooms: '9',
    sleeps: 20,
    beachfront: false,
    privatePool: true,
    golfCart: false,
    bikes: false,
    elevator: true,
    rooftop: true,
    gulfViews: true,
    carriageHouse: false,
    summerKitchen: false,
    outdoorLiving: false,
    highlights: [
      '7 bedrooms',
      '9 bathrooms',
      'Sleeps up to 20',
      'Private pool',
      'Private elevator',
      'Rooftop outdoor living with Gulf views',
    ],
    verifiedAmenityPhrases: [
      'a private pool on Level 2',
      'a private elevator',
      'a 1000 sq. ft. rooftop outdoor living area with 180-degree Gulf views',
      'private parking for two vehicles including a private garage',
      'a primary suite with two private bathrooms, a sitting room, and a balcony overlooking Rosemary Beach Town Center',
      'a central Rosemary Beach location',
      'pet friendly with an additional fee and pre-approval',
    ],
    summarySentence:
      'sleeps up to 20 and offers a private pool, a private elevator, and a rooftop living area with Gulf views',
    image:
      'https://storage.helloreaddy.io/project_files/6010ef82-6c94-407b-9fac-7f3ca6010bf2/576540c6-340c-4c96-bfe4-92419b9eb9f0_compressed_BELVEDERE-West-Gathering-Area-1_1.webp',
    tag: 'Rooftop estate',
    blurb:
      'Seven bedrooms, a private pool, a private elevator, and a rooftop with Gulf views. Built for a family that arrives together.',
    sourceUrl: 'https://inspirevp.com/the-belvedere',
    petPolicy: 'Pet friendly with an additional fee per pet and pre-approval required.',
    status: 'verified',
  },
  {
    id: 'hannahs-pearl',
    name: 'Hannah\u2019s Pearl',
    location: 'WaterSound West / 30A',
    area: '30A',
    communities: ['30A', 'Santa Rosa Beach', 'WaterSound'],
    bedrooms: 5,
    bathrooms: '5.5',
    sleeps: 12,
    beachfront: false,
    privatePool: true,
    golfCart: true,
    bikes: true,
    elevator: false,
    rooftop: false,
    gulfViews: false,
    carriageHouse: true,
    summerKitchen: true,
    outdoorLiving: true,
    highlights: [
      '5 bedrooms',
      '5.5 bathrooms',
      'Sleeps up to 12',
      'Private pool',
      'Carriage house',
      'Outdoor living space',
      'Summer kitchen',
      'Bikes',
      'Golf cart',
    ],
    verifiedAmenityPhrases: [
      'an oversized custom private pool with a sun shelf',
      'a carriage house with a private entrance, living area, king bedroom, kitchen, and full bathroom',
      'outdoor living with a fire pit and summer kitchen',
      'a gas grill, Traeger wood-fired smoker, and Big Green Egg',
      'two screened porches',
      'four bicycles included with rental',
      'a golf cart included with every rental',
      'Sonos sound throughout the property',
      'WaterSound West community amenities and private beach access',
    ],
    summarySentence:
      'sleeps up to 12 and offers a private pool, a carriage house, an outdoor living space, a summer kitchen, bikes, and a golf cart',
    image:
      'https://storage.helloreaddy.io/project_files/6010ef82-6c94-407b-9fac-7f3ca6010bf2/76a7960c-3936-4388-8db6-dfddaaa190a0_compressed_hannahs-pearl1_1.webp',
    tag: 'Family compound',
    blurb:
      'Main house plus carriage house, a private pool, a summer kitchen, bikes and a golf cart. A calmer footprint on the 30A rhythm.',
    sourceUrl: 'https://inspirevp.com/hannahs-pearl',
    status: 'verified',
  },
  {
    id: 'sea-dreams',
    name: 'Sea Dreams',
    location: 'Thomas Drive, Panama City Beach',
    area: 'Panama City Beach',
    communities: ['Panama City Beach'],
    bedrooms: 5,
    bathrooms: '5',
    sleeps: 16,
    beachfront: true,
    privatePool: true,
    golfCart: false,
    bikes: false,
    elevator: false,
    rooftop: false,
    gulfViews: true,
    carriageHouse: false,
    summerKitchen: false,
    outdoorLiving: false,
    highlights: [
      '5 bedrooms',
      '5 bathrooms',
      'Sleeps up to 16',
      'Beachfront',
      'Private beachfront pool',
      'Gulf views from all three levels',
    ],
    verifiedAmenityPhrases: [
      'a Gulf-front private pool',
      'Gulf views from all three levels',
      'beach access just steps away',
      'parking for up to 3 vehicles depending on size and type',
      'complimentary Wi-Fi, washer and dryer, and a gas grill',
      'an extra refrigerator in the garage',
      'a pack and play, highchair, X-Box, and board games',
      'a Thomas Drive location near restaurants, bars, shopping, Pier Park, and sports complexes',
    ],
    summarySentence:
      'sleeps up to 16, is beachfront, and offers a private beachfront pool and Gulf views from all three levels',
    image:
      'https://storage.helloreaddy.io/project_files/6010ef82-6c94-407b-9fac-7f3ca6010bf2/55109b28-0159-4c5d-9fdb-f53f58dcaec8_compressed_sea-dreams-31.webp',
    tag: 'Beachfront',
    blurb:
      'Sand at your feet on Thomas Drive. A private beachfront pool and Gulf views from every level.',
    sourceUrl: 'https://inspirevp.com/sea-dreams',
    status: 'verified',
  },
  {
    id: 'beach-beach-baby',
    name: 'Beach Beach Baby',
    location: 'Panama City Beach',
    area: 'Panama City Beach',
    communities: ['Panama City Beach'],
    bedrooms: 9,
    bathrooms: '11',
    sleeps: 28,
    beachfront: false,
    privatePool: true,
    golfCart: false,
    bikes: false,
    elevator: true,
    rooftop: false,
    gulfViews: true,
    carriageHouse: false,
    summerKitchen: false,
    outdoorLiving: true,
    highlights: [
      '9 bedrooms',
      '11 bathrooms',
      'Sleeps up to 28',
      'Private pool and hot tub',
      'Elevator',
      'Outdoor living areas with Gulf views',
    ],
    verifiedAmenityPhrases: [
      'new construction built in 2023',
      'over 5200 sq. ft. across three levels',
      'an elevator servicing all three levels',
      'massive outdoor balconies on Level 2 and Level 3 with sweeping Gulf views',
      'a private pool and hot tub',
      'beach access just out the front door',
      'a West End Panama City Beach location',
      'parking for 6 to 8 vehicles depending on size and type',
      'pet friendly with an additional fee',
    ],
    summarySentence:
      'sleeps up to 28 and offers a private pool, hot tub, elevator, outdoor living areas, and Gulf views',
    image: 'https://inspirevp.com/wp-content/uploads/2023/08/1.jpg',
    tag: 'Large group retreat',
    blurb:
      'A 2023 coastal home with nine bedrooms, a private pool and hot tub, elevator, and room for the whole group.',
    sourceUrl: 'https://inspirevp.com/beach-beach-baby',
    petPolicy: 'Pet friendly with an additional fee.',
    status: 'verified',
  },
  {
    id: 'bella-vita',
    name: 'Bella Vita',
    location: 'Emerald Beach Resort, Panama City Beach',
    area: 'Panama City Beach',
    communities: ['Panama City Beach'],
    bedrooms: 2,
    bathrooms: '2',
    sleeps: 6,
    beachfront: true,
    privatePool: false,
    golfCart: false,
    bikes: false,
    elevator: false,
    rooftop: false,
    gulfViews: true,
    carriageHouse: false,
    summerKitchen: false,
    outdoorLiving: false,
    highlights: [
      '2 bedrooms',
      '2 bathrooms',
      'Sleeps up to 6',
      'Gulf-front condo',
      'Massive balcony',
      'Emerald Beach Resort amenities',
    ],
    verifiedAmenityPhrases: [
      'a Gulf-front penthouse condo at Emerald Beach Resort',
      'a massive balcony overlooking the Gulf of Mexico',
      'several pools, a hot tub, and splash pad',
      'a fitness center',
      'a covered parking garage with connected sky bridge',
      'The Sky Bar and on-site Starbucks',
      'two beach chairs and umbrella setup included March 1 through October 31',
      'a Panama City Beach location near Pier Park, Shipwreck Island, restaurants, bars, and shopping',
    ],
    summarySentence:
      'sleeps up to 6 and offers a Gulf-front condo setting, a massive balcony, and Emerald Beach Resort amenities',
    image: 'https://inspirevp.com/wp-content/uploads/2026/08/1.jpg',
    tag: 'Gulf-front condo',
    blurb:
      'A renovated Gulf-front penthouse condo with sweeping views and resort amenities in the heart of Panama City Beach.',
    sourceUrl: 'https://inspirevp.com/bella-vita',
    status: 'verified',
  },
  {
    id: 'sunset-bungalow-30a',
    name: 'Sunset Bungalow on 30A',
    location: 'Dune Allen / 30A',
    area: '30A',
    communities: ['30A', 'Dune Allen', 'Santa Rosa Beach'],
    bedrooms: 4,
    bathrooms: '2',
    sleeps: 10,
    beachfront: false,
    privatePool: false,
    golfCart: false,
    bikes: true,
    elevator: false,
    rooftop: false,
    gulfViews: true,
    carriageHouse: false,
    summerKitchen: false,
    outdoorLiving: true,
    highlights: [
      '4 bedrooms',
      '2 bathrooms',
      'Sleeps up to 10',
      'Beach access steps away',
      'Gulf views',
      '4 bikes included',
    ],
    verifiedAmenityPhrases: [
      'beach access just steps away from the backyard',
      'Gulf views',
      'four bicycles included',
      'top-of-the-line stainless appliances',
      'an outdoor propane gas grill',
      'an outdoor shower',
      'on-site parking for up to 4 vehicles',
      'a front porch overlooking Allen Lake',
      'a Dune Allen location across from Stinky’s Fish Camp',
      'pet friendly with an additional fee and prior approval',
    ],
    summarySentence:
      'sleeps up to 10 and offers beach access steps away, Gulf views, outdoor living, and four bikes',
    image: 'https://inspirevp.com/wp-content/uploads/2022/11/sunset-bungalow-1.jpg',
    tag: '30A beach cottage',
    blurb:
      'A 30A cottage in Dune Allen with beach access just steps away, Gulf views, outdoor space, and four bikes.',
    sourceUrl: 'https://inspirevp.com/sunset-bungalow-30a',
    petPolicy: 'Pet friendly with an additional fee and prior approval.',
    status: 'verified',
  },
];

export const experienceCategories = [
  {
    id: 'gulf-front',
    label: 'Gulf Front',
    tagline: 'Emerald Green waters and sugar white sand steps away.',
    image:
      'https://storage.helloreaddy.io/project_files/6010ef82-6c94-407b-9fac-7f3ca6010bf2/9e8238e3-452c-4f46-85a5-7c0c0427902b_compressed_indian-summer-4715-Thomas-Dr-Unit-907-23.webp',
  },
  {
    id: 'pool',
    label: 'Has a Pool',
    tagline: 'Pool time, built into the stay.',
    image:
      'https://storage.helloreaddy.io/project_files/6010ef82-6c94-407b-9fac-7f3ca6010bf2/0aa5edf6-405a-44cf-8040-23c40af2b300_compressed_sweet-caroline-pool1.webp',
  },
  {
    id: 'large-groups',
    label: 'Large Groups',
    tagline: 'Room for everyone, together.',
    image:
      'https://storage.helloreaddy.io/project_files/6010ef82-6c94-407b-9fac-7f3ca6010bf2/783cec76-5abc-4c89-b203-e33cde7ac8be_compressed_Kitchen-Island-1.webp',
  },
  {
    id: 'outdoor-living',
    label: 'Outdoor Living',
    tagline: 'You\'re on vacation, get outside.',
    image:
      'https://storage.helloreaddy.io/project_files/6010ef82-6c94-407b-9fac-7f3ca6010bf2/0ae6af25-305e-46b6-a249-edb02a069d53_compressed_11_1.webp',
  },
  {
    id: 'golf-cart',
    label: 'Has a Golf Cart',
    tagline: 'Getting around is fun and easy on your very own golf cart.',
    image:
      'https://storage.helloreaddy.io/project_files/6010ef82-6c94-407b-9fac-7f3ca6010bf2/4e532870-9fa3-4a71-aa42-c23fbc9de7c6_compressed_IMG_8217.webp',
  },
  {
    id: 'pet-friendly',
    label: 'Pet Friendly',
    tagline: 'Pet-friendly stays for guests bringing the whole family along.',
    image: 'https://inspirevp.com/wp-content/uploads/2022/11/sunset-bungalow-1.jpg',
  },
];
